import React from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export interface FilterChipOption {
  /** Empty string means "no filter". */
  value: string;
  label: string;
}

interface FilterChipProps {
  /** Field name shown before the selected value, e.g. "Provider". */
  label: string;
  value: string;
  options: FilterChipOption[];
  onChange: (value: string) => void;
}

/**
 * A dropdown that reads as a filter rather than a search field. The visual
 * difference matters: filters narrow whatever the search returned, so they must
 * not look like another place to type a term.
 */
export const FilterChip: React.FC<FilterChipProps> = ({
  label,
  value,
  options,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const selected = options.find((option) => option.value === value);

  const handleSelect = (next: string) => {
    setAnchorEl(null);
    if (next !== value) {
      onChange(next);
    }
  };

  return (
    <>
      <Chip
        variant={value ? "filled" : "outlined"}
        color={value ? "primary" : "default"}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-haspopup="listbox"
        aria-expanded={open}
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {`${label}: ${selected?.label ?? options[0]?.label ?? "Any"}`}
            <ExpandMoreIcon fontSize="small" />
          </Box>
        }
      />
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ role: "listbox" }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => handleSelect(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
